import { PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { media } from '@myrealtrip/modash';
import { UnstyledButton, Text } from '@myrealtrip/web-ui';

type Tab<T> = {
  key: T;
  label: string;
  tabProps?: Omit<React.ComponentProps<typeof TabButton>, 'isSelected' | 'scrollable' | 'onClick'>;
  textProps?: Omit<React.ComponentProps<typeof Text>, 'children'>;
};

interface Props<T> {
  className?: string;
  scrollable?: boolean;
  tabs: Tab<T>[];
  selectedTabKey: T;
  onChangeTab: (key: T, label: string) => void;
}

const TabBar = <T extends string | number>({
  className,
  scrollable = false,
  tabs,
  selectedTabKey,
  onChangeTab,
  children,
}: PropsWithChildren<Props<T>>) => {
  const handleChangeTab = (key: T, label: string) => {
    onChangeTab(key, label);
  };

  const Wrapper = scrollable ? ScrollableTabWrapper : FixedTabWrapper;

  return (
    <Container className={className}>
      <Wrapper>
        {tabs.map((tab) => (
          <TabButton
            key={tab.key}
            isSelected={selectedTabKey === tab.key}
            scrollable={scrollable}
            onClick={() => handleChangeTab(tab.key, tab.label)}
            {...tab.tabProps}
          >
            <TabButtonText typography="headlineNormal15" {...tab.textProps}>
              {tab.label}
            </TabButtonText>
          </TabButton>
        ))}
      </Wrapper>
      {children}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ScrollableTabWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  padding: 0 16px;
  border-bottom: 2px solid #f5f5f5;
  overflow-x: auto;

  ${media.mobile} {
    scroll-snap-type: x mandatory;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const FixedTabWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px;

  ${media.mobile} {
    padding: 0;
  }
`;

const TabButton = styled(UnstyledButton)<{ isSelected: boolean; scrollable: boolean }>`
  position: relative;
  padding: 15px 0 16px;
  margin: 0 14px !important;

  ${({ scrollable }) =>
    scrollable
      ? css`
          min-width: fit-content;
        `
      : css`
          flex: 1;
        `};

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        display: block;
        width: 100%;
        height: 2.5px;
        background-color: ${theme.colors.gray.s1000};
        border-radius: 1.5px 1.5px 0 0;
      }
    `};

  ${media.mobile} {
    padding: 15px 12px 16px;
    margin: 0 12px !important;
  }
`;

const TabButtonText = styled(Text)`
  color: ${({ theme: { colors } }) => colors.gray.s1000};
  text-align: center;
`;

export default TabBar;
